/**
 * JulietteOrb — Signature visuelle de Juliette (V3.5).
 *
 * Orb animé inspiré Apple Intelligence (Robin Holesinsky, mood Siri 2024+).
 * Composant unique réutilisé partout : tab bar mobile center, widget desktop
 * bas-droite, header / drawer / dialogs (remplace la mascotte fourmi),
 * fullscreen voice mode (PR3).
 *
 * 4 états : idle (respiration lente) · listening (pulse rapide) ·
 *            thinking (arc rotatif) · speaking (déformation cyclée).
 * 4 tailles : sm 40px · md 48px · lg 56px (tab bar) · xl 240px (fullscreen).
 *
 * Effet visuel : 2 halos externes déphasés + corps principal avec gradient
 * radial multi-stops + 2 blobs internes mobiles (effet plasma liquide) +
 * reflet glossy. Tout en SVG + Framer Motion (0 ko de bundle ajouté,
 * compat Safari iOS PWA, animations transform/opacity GPU-only).
 */

import { memo } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export type OrbState = "idle" | "listening" | "thinking" | "speaking" | "alert" | "done" | "standby";
export type OrbSize = "sm" | "md" | "lg" | "xl";

/**
 * Identifiant de palette : ambre = "quelque chose pour toi" (signal en attente),
 * vert = "fait" (flash bref de confirmation), indigo = repos/conversation.
 */
export type OrbPaletteId = "indigo" | "amber" | "green";

/** Palette de l'orbe selon l'etat. */
export function orbPaletteId(state: OrbState): OrbPaletteId {
  if (state === "alert") return "amber";
  if (state === "done") return "green";
  return "indigo";
}

/** Stops de degrade (offset, couleur) par palette, pour le corps et les 2 halos. */
const ORB_PALETTES: Record<
  OrbPaletteId,
  {
    body: Array<[string, string]>;
    glow1: Array<[string, string, string]>;
    glow2: Array<[string, string, string]>;
    /** Couleur du bloom (drop-shadow) quand l'orbe est en mode `glow` (présence). */
    bloom: string;
  }
> = {
  indigo: {
    body: [
      ["0%", "#dbeafe"],
      ["20%", "#a5b4fc"],
      ["50%", "#6366f1"],
      ["80%", "#4338ca"],
      ["100%", "#1e1b4b"],
    ],
    glow1: [
      ["0%", "#818cf8", "0.9"],
      ["55%", "#6366f1", "0.4"],
      ["100%", "#6366f1", "0"],
    ],
    glow2: [
      ["0%", "#c084fc", "0.6"],
      ["65%", "#a855f7", "0.2"],
      ["100%", "#a855f7", "0"],
    ],
    bloom: "#818cf8",
  },
  amber: {
    body: [
      ["0%", "#fef3c7"],
      ["20%", "#fcd34d"],
      ["50%", "#f59e0b"],
      ["80%", "#b45309"],
      ["100%", "#451a03"],
    ],
    glow1: [
      ["0%", "#fbbf24", "0.95"],
      ["55%", "#f59e0b", "0.45"],
      ["100%", "#f59e0b", "0"],
    ],
    glow2: [
      ["0%", "#fdba74", "0.6"],
      ["65%", "#fb923c", "0.22"],
      ["100%", "#fb923c", "0"],
    ],
    bloom: "#fbbf24",
  },
  green: {
    body: [
      ["0%", "#dcfce7"],
      ["20%", "#86efac"],
      ["50%", "#22c55e"],
      ["80%", "#15803d"],
      ["100%", "#052e16"],
    ],
    glow1: [
      ["0%", "#4ade80", "0.95"],
      ["55%", "#22c55e", "0.5"],
      ["100%", "#22c55e", "0"],
    ],
    glow2: [
      ["0%", "#86efac", "0.6"],
      ["65%", "#34d399", "0.22"],
      ["100%", "#34d399", "0"],
    ],
    bloom: "#4ade80",
  },
};

interface JulietteOrbProps {
  state?: OrbState;
  /** Variante predefinie OU taille pixel custom (utile pour avatars de tailles arbitraires). */
  size?: OrbSize | number;
  onActivate?: () => void;
  hasPendingSession?: boolean;
  ariaLabel?: string;
  className?: string;
  /** Rend l'orb sans bouton (utile pour les avatars statiques dans messages/drawer). */
  asAvatar?: boolean;
  /** Mode présence (sidebar/panneau) : halo plus lumineux + bloom. Off par défaut
   *  pour garder les mini-avatars (messages, drawers) discrets. */
  glow?: boolean;
}

const SIZE_PX: Record<OrbSize, number> = {
  sm: 40,
  md: 48,
  lg: 56,
  xl: 240,
};

function resolvePx(size: OrbSize | number): number {
  return typeof size === "number" ? size : SIZE_PX[size];
}

function resolveSizeKey(size: OrbSize | number): string {
  return typeof size === "number" ? `px${size}` : size;
}

// Tempo selon l'état (durée d'un cycle d'animation principal en secondes).
function getTempo(state: OrbState): number {
  switch (state) {
    case "listening":
      return 1;
    case "speaking":
      return 0.8;
    case "thinking":
      return 1.6;
    case "alert":
      return 1.8;
    case "done":
      return 0.6;
    case "standby":
      // Veille : respiration calme « éveillée, en attente » (entre idle et listening).
      return 2.4;
    case "idle":
    default:
      return 3;
  }
}

// Animation du corps principal.
function bodyAnimate(state: OrbState) {
  const duration = getTempo(state);
  switch (state) {
    case "speaking":
      return {
        scale: [1, 1.1, 0.97, 1.07, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
    case "alert":
      return {
        scale: [1, 1.07, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
    case "done":
      // Pop bref de confirmation (pas de repeat : le flash est piloté côté parent).
      return {
        scale: [1, 1.18, 1] as number[],
        transition: { duration, ease: "easeOut" as const },
      };
    case "listening":
      return {
        scale: [1, 1.06, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
    case "thinking":
      return {
        scale: [1, 1.02, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
    case "standby":
      // Respiration douce, un peu plus ample que l'idle → « je suis là, en veille ».
      return {
        scale: [1, 1.05, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
    case "idle":
    default:
      return {
        scale: [1, 1.04, 1] as number[],
        transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
      };
  }
}

// Multiplicateur d'opacité du halo en mode présence (sidebar/panneau) : rend la
// lueur nettement plus lumineuse au repos. Réglable à l'œil.
const GLOW_OPACITY_BOOST = 1.4;

// Halo externe : pulse + opacité. 3 intensités : `fast` (états actifs/alert/done),
// `standby` (veille : présent mais calme), `rest` (idle/thinking). `glow` (présence)
// pousse l'opacité pour une lueur plus marquée — les mini-avatars (sans glow) restent
// discrets.
function haloAnimate(state: OrbState, pending: boolean, phase: 0 | 1, glow: boolean) {
  const fast =
    state === "listening" || state === "speaking" || state === "alert" || state === "done" || pending;
  const tier: "fast" | "standby" | "rest" = fast ? "fast" : state === "standby" ? "standby" : "rest";

  // 2 halos déphasés : phase 0 = base, phase 1 = décalé pour effet dimensionnel.
  const baseDuration = tier === "fast" ? 1.2 : tier === "standby" ? 2 : 2.6;

  const scale =
    phase === 0
      ? tier === "fast"
        ? [1, 1.18, 1]
        : tier === "standby"
          ? [1, 1.14, 1]
          : [1, 1.08, 1]
      : tier === "fast"
        ? [1.05, 1.22, 1.05]
        : tier === "standby"
          ? [1.04, 1.18, 1.04]
          : [1.04, 1.12, 1.04];

  const baseOpacity =
    phase === 0
      ? tier === "fast"
        ? [0.55, 0.25, 0.55]
        : tier === "standby"
          ? [0.5, 0.28, 0.5]
          : [0.4, 0.2, 0.4]
      : tier === "fast"
        ? [0.3, 0.1, 0.3]
        : tier === "standby"
          ? [0.28, 0.12, 0.28]
          : [0.25, 0.1, 0.25];
  const opacity = glow
    ? baseOpacity.map((o) => Math.min(0.92, o * GLOW_OPACITY_BOOST))
    : baseOpacity;

  return {
    scale: scale as number[],
    opacity: opacity as number[],
    transition: {
      duration: baseDuration,
      repeat: Infinity,
      ease: "easeInOut" as const,
      delay: phase === 1 ? baseDuration / 3 : 0,
    },
  };
}

// Blob interne mobile (effet plasma). Anime translation X/Y + scale.
function blobAnimate(state: OrbState, variant: "a" | "b") {
  const duration = state === "speaking" ? 1.6 : state === "listening" ? 2.2 : 4.5;
  // 2 variantes déphasées avec trajectoires opposées.
  if (variant === "a") {
    return {
      x: [0, 5, -3, 2, 0],
      y: [0, -4, 3, -1, 0],
      scale: [1, 1.15, 0.9, 1.08, 1],
      transition: { duration, repeat: Infinity, ease: "easeInOut" as const },
    };
  }
  return {
    x: [0, -4, 3, -2, 0],
    y: [0, 3, -4, 2, 0],
    scale: [1, 0.92, 1.12, 0.95, 1],
    transition: {
      duration: duration * 1.1,
      repeat: Infinity,
      ease: "easeInOut" as const,
      delay: duration / 2,
    },
  };
}

interface OrbVisualProps {
  state: OrbState;
  px: number;
  sizeKey: string;
  hasPendingSession: boolean;
  /** Mode présence : halo plus lumineux + bloom (drop-shadow). */
  glow: boolean;
}

function OrbVisual({ state, px, sizeKey, hasPendingSession, glow }: OrbVisualProps) {
  const bodyId = `juliette-orb-body-${sizeKey}`;
  const glow1Id = `juliette-orb-glow1-${sizeKey}`;
  const glow2Id = `juliette-orb-glow2-${sizeKey}`;
  const blobAId = `juliette-orb-blobA-${sizeKey}`;
  const blobBId = `juliette-orb-blobB-${sizeKey}`;
  const palette = ORB_PALETTES[orbPaletteId(state)];

  return (
    <svg
      viewBox="0 0 100 100"
      width={px}
      height={px}
      aria-hidden="true"
      style={{
        overflow: "visible",
        // Bloom lumineux en mode présence (rayon proportionnel à la taille).
        filter: glow
          ? `drop-shadow(0 0 ${Math.round(px * 0.14)}px ${palette.bloom})`
          : undefined,
      }}
    >
      <defs>
        {/* Corps : dégradé radial profond (indigo par défaut, ambre si état alert) */}
        <radialGradient id={bodyId} cx="38%" cy="32%" r="78%">
          {palette.body.map(([offset, color]) => (
            <stop key={offset} offset={offset} stopColor={color} />
          ))}
        </radialGradient>
        {/* Halo 1 : flou doux */}
        <radialGradient id={glow1Id} cx="50%" cy="50%" r="50%">
          {palette.glow1.map(([offset, color, opacity]) => (
            <stop key={offset} offset={offset} stopColor={color} stopOpacity={opacity} />
          ))}
        </radialGradient>
        {/* Halo 2 : plus tendu, déphasé */}
        <radialGradient id={glow2Id} cx="50%" cy="50%" r="55%">
          {palette.glow2.map(([offset, color, opacity]) => (
            <stop key={offset} offset={offset} stopColor={color} stopOpacity={opacity} />
          ))}
        </radialGradient>
        {/* Blob A : cyan lumineux */}
        <radialGradient id={blobAId} cx="40%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#e0f2fe" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#7dd3fc" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
        </radialGradient>
        {/* Blob B : magenta doux */}
        <radialGradient id={blobBId} cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#fce7f3" stopOpacity="0.8" />
          <stop offset="60%" stopColor="#f0abfc" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#d946ef" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Halo externe 1 (large, lent) */}
      <motion.circle
        cx="50"
        cy="50"
        r="42"
        fill={`url(#${glow1Id})`}
        animate={haloAnimate(state, hasPendingSession, 0, glow)}
        style={{ transformOrigin: "50% 50%", transformBox: "fill-box" }}
      />

      {/* Halo externe 2 (déphasé, violet) */}
      <motion.circle
        cx="50"
        cy="50"
        r="44"
        fill={`url(#${glow2Id})`}
        animate={haloAnimate(state, hasPendingSession, 1, glow)}
        style={{ transformOrigin: "50% 50%", transformBox: "fill-box" }}
      />

      {/* Corps principal */}
      <motion.circle
        cx="50"
        cy="50"
        r="32"
        fill={`url(#${bodyId})`}
        animate={bodyAnimate(state)}
        style={{ transformOrigin: "50% 50%", transformBox: "fill-box" }}
      />

      {/* Blob plasma A (cyan, orbite haut-gauche) */}
      <motion.circle
        cx="42"
        cy="44"
        r="10"
        fill={`url(#${blobAId})`}
        animate={blobAnimate(state, "a")}
        style={{ transformOrigin: "42px 44px", transformBox: "fill-box", mixBlendMode: "screen" }}
      />

      {/* Blob plasma B (magenta, orbite bas-droite) */}
      <motion.circle
        cx="58"
        cy="56"
        r="8"
        fill={`url(#${blobBId})`}
        animate={blobAnimate(state, "b")}
        style={{ transformOrigin: "58px 56px", transformBox: "fill-box", mixBlendMode: "screen" }}
      />

      {/* Reflet glossy haut-gauche (statique) */}
      <ellipse cx="40" cy="34" rx="11" ry="6" fill="white" opacity="0.35" />
      <circle cx="44" cy="32" r="2.2" fill="white" opacity="0.6" />

      {/* Arc rotatif quand thinking */}
      {state === "thinking" && (
        <motion.circle
          cx="50"
          cy="50"
          r="38"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="55 200"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50% 50%", transformBox: "fill-box" }}
        />
      )}
    </svg>
  );
}

export const JulietteOrb = memo(function JulietteOrb({
  state = "idle",
  size = "md",
  onActivate,
  hasPendingSession = false,
  ariaLabel = "Ouvrir Juliette",
  className,
  asAvatar = false,
  glow = false,
}: JulietteOrbProps) {
  const px = resolvePx(size);
  const sizeKey = resolveSizeKey(size);

  // Variante avatar statique : rend juste le visuel sans bouton (pour les
  // headers de message, drawers, dialogs où la fourmi était utilisée).
  if (asAvatar) {
    return (
      <div
        className={cn("relative inline-flex shrink-0 items-center justify-center", className)}
        style={{ width: px, height: px }}
        aria-label={ariaLabel}
        role="img"
      >
        <OrbVisual state={state} px={px} sizeKey={sizeKey} hasPendingSession={hasPendingSession} glow={glow} />
        {hasPendingSession && (
          <span
            aria-hidden
            className="absolute top-0 right-0 h-3 w-3 rounded-full bg-destructive ring-2 ring-background"
          />
        )}
      </div>
    );
  }

  const finalLabel = hasPendingSession
    ? `${ariaLabel} — notification`
    : ariaLabel;

  return (
    <motion.button
      type="button"
      onClick={onActivate}
      aria-label={finalLabel}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.92 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className,
      )}
      style={{ width: px, height: px }}
    >
      <OrbVisual state={state} px={px} sizeKey={sizeKey} hasPendingSession={hasPendingSession} />
      {hasPendingSession && (
        <span
          aria-hidden
          className="absolute top-0 right-0 h-3 w-3 rounded-full bg-destructive ring-2 ring-background"
        />
      )}
    </motion.button>
  );
});
